import { inject } from "vue";
import { Contacts } from "acquaint-api/models/Contacts";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { IdNamePair } from "@/types/generic/IdNamePair";

export function useContactHelper() {
  const acquaintApiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;
  const apiNextClient = inject<AcquaintApiNextClient>("AcquaintApiNextClient")!;

  const fetchContactById = async (
    contactId: number,
  ): Promise<Contacts | undefined> => {
    try {
      if (contactId > 0) {
        const contact = await acquaintApiClient.httpGet<Contacts>(
          `database/contacts/${contactId}`,
        );
        return contact;
      }
    } catch (ex) {
      displayErrorMessage(getErrorMessage(ex));
      return undefined;
    }
  };

  const updateContact = async (contact: Contacts): Promise<void> => {
    try {
      if (!contact || !contact.id) return;

      await acquaintApiClient.httpPut<Contacts>(
        `database/contacts/${contact.id}`,
        contact,
      );
    } catch (err) {
      displayErrorMessage(getErrorMessage(err));
      throw err;
    }
  };

  const getSystemContactTypeByTypeId = async (
    contactTypeId: number,
  ): Promise<number> => {
    try {
      const type = await acquaintApiClient.httpGet<number>(
        `app/contacts/${contactTypeId}/systemcontacttype`,
      );
      return type;
    } catch (err) {
      displayErrorMessage(getErrorMessage(err));
      throw err;
    }
  };

  const fetchLinkedContacts = async (contactId: number) => {
    try {
      const linkedContacts = await apiNextClient.httpGet<IdNamePair[]>(
        `/contacts/${contactId}/links`,
      );
      return linkedContacts;
    } catch (err) {
      displayErrorMessage("The linked contacts could not be fetched");
      return [];
    }
  };

  return {
    fetchContactById,
    updateContact,
    getSystemContactTypeByTypeId,
    fetchLinkedContacts,
  };
}
